import { mulberry32 } from '../core/rng.js';
import {
    stampPoplar, stampWillow, stampSheep, stampFence, scatterReeds, scatterTulips,
} from './props.js';

// ---------------------------------------------------------------------------
// What is behind the village.
//
// Volendam is a harbour with a strip of houses along the dijk and flat polder
// behind it — so the empty green is not wrong, but twenty-five units of
// nothing reads as unfinished rather than pastoral. This puts the same
// furniture the Zaanse Schans has on the other islands: hedged fields, sheep,
// a line of poplars along a track, reeds where the land meets the water.
//
// All of it is the props the village already uses. Nothing new is drawn.
// ---------------------------------------------------------------------------

export function dressCountryside(grid, P, polder, seed, { keepClear = [] } = {}) {
    const rng = mulberry32(seed);
    const { size } = polder;
    const mid = size / 2;

    const blocked = (x, z) =>
        keepClear.some((c) => (x - c.x) ** 2 + (z - c.z) ** 2 < c.r * c.r);

    // Somewhere well inland, on grass, out of everyone's way.
    const inland = (x, z) =>
        polder.isDryLand(x, z) && !blocked(x, z)
        && Math.hypot(x - mid, z - mid) < size * 0.40;

    // --- fields, hedged and grazed ---------------------------------------
    for (let f = 0; f < 7; f++) {
        const w = 7 + Math.floor(rng() * 7);
        const d = 6 + Math.floor(rng() * 6);
        const x0 = Math.round(mid + (rng() - 0.5) * size * 0.55);
        const z0 = Math.round(mid + (rng() - 0.5) * size * 0.55);
        const x1 = x0 + w;
        const z1 = z0 + d;
        // Only fence a field that is all dry land, or the fence walks into
        // the sea and looks like a mistake, which it would be.
        let ok = true;
        for (let z = z0; z <= z1 && ok; z++) {
            for (let x = x0; x <= x1 && ok; x++) if (!inland(x, z)) ok = false;
        }
        if (!ok) continue;

        stampFence(grid, P, polder, x0, z0, x1, z0);
        stampFence(grid, P, polder, x0, z1, x1, z1);
        stampFence(grid, P, polder, x0, z0, x0, z1);
        stampFence(grid, P, polder, x1, z0, x1, z1);

        const flock = 1 + Math.floor(rng() * 3);
        for (let i = 0; i < flock; i++) {
            const sx = x0 + 1 + Math.floor(rng() * (w - 1));
            const sz = z0 + 1 + Math.floor(rng() * (d - 1));
            if (inland(sx, sz)) stampSheep(grid, P, polder, sx, sz, rng() * Math.PI * 2, rng);
        }
    }

    // --- a line of poplars, the way a Dutch road announces itself ---------
    const roadA = rng() * Math.PI * 2;
    for (let i = 0; i < 16; i++) {
        const r = size * 0.08 + i * size * 0.022;
        const x = Math.round(mid + Math.sin(roadA) * r);
        const z = Math.round(mid + Math.cos(roadA) * r);
        if (!inland(x, z) || rng() < 0.2) continue;
        stampPoplar(grid, P, polder, x, z, rng);
    }

    // --- willows in the damp corners --------------------------------------
    for (let i = 0; i < 14; i++) {
        const x = Math.round(mid + (rng() - 0.5) * size * 0.7);
        const z = Math.round(mid + (rng() - 0.5) * size * 0.7);
        if (!inland(x, z)) continue;
        stampWillow(grid, P, polder, x, z, rng);
    }

    scatterReeds(grid, P, polder, seed + 31);
    scatterTulips(grid, P, polder, seed + 57);
}
